import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

import Input from '../../../components/ui/Input';

const ReportBuilderPanel = () => {
  const [reportName, setReportName] = useState('');
  const [selectedDataSources, setSelectedDataSources] = useState([]);
  const [selectedVisualizations, setSelectedVisualizations] = useState([]);
  const [reportLayout, setReportLayout] = useState([]);
  const [exportFormat, setExportFormat] = useState('pdf');

  const dataSources = [
    { id: 'placements', label: 'Placement Data', icon: 'Briefcase' },
    { id: 'applications', label: 'Application Records', icon: 'FileText' },
    { id: 'interviews', label: 'Interview Schedules', icon: 'Calendar' },
    { id: 'employers', label: 'Employer Database', icon: 'Building2' },
    { id: 'students', label: 'Student Profiles', icon: 'Users' },
    { id: 'salaries', label: 'Salary Information', icon: 'DollarSign' }
  ];

  const visualizationTypes = [
    { id: 'bar-chart', label: 'Bar Chart', icon: 'BarChart3', description: 'Compare values across categories' },
    { id: 'line-chart', label: 'Line Chart', icon: 'LineChart', description: 'Show trends over time' },
    { id: 'pie-chart', label: 'Pie Chart', icon: 'PieChart', description: 'Display proportions' },
    { id: 'table', label: 'Data Table', icon: 'Table', description: 'Detailed data view' },
    { id: 'stat-card', label: 'Stat Cards', icon: 'LayoutGrid', description: 'Key metrics display' },
    { id: 'heatmap', label: 'Heat Map', icon: 'Grid3x3', description: 'Pattern visualization' }
  ];

  const reportTemplates = [
    {
      id: 'executive',
      name: 'Executive Summary',
      description: 'High-level overview with key metrics',
      components: ['stat-card', 'bar-chart', 'pie-chart']
    },
    {
      id: 'detailed',
      name: 'Detailed Analytics',
      description: 'Comprehensive data analysis',
      components: ['table', 'line-chart', 'bar-chart', 'stat-card']
    },
    {
      id: 'trends',
      name: 'Trend Analysis',
      description: 'Time-based performance tracking',
      components: ['line-chart', 'heatmap', 'stat-card']
    }
  ];

  const exportFormatOptions = [
    { value: 'pdf', label: 'PDF Document' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'csv', label: 'CSV File' },
    { value: 'json', label: 'JSON Data' }
  ];

  const handleDataSourceToggle = (sourceId) => {
    setSelectedDataSources(prev => 
      prev?.includes(sourceId) 
        ? prev?.filter(id => id !== sourceId)
        : [...prev, sourceId]
    );
  };

  const handleVisualizationToggle = (vizId) => {
    setSelectedVisualizations(prev => 
      prev?.includes(vizId) 
        ? prev?.filter(id => id !== vizId)
        : [...prev, vizId]
    );
  };

  const handleAddToLayout = (vizId) => {
    const viz = visualizationTypes?.find(v => v?.id === vizId);
    if (viz && !reportLayout?.some(item => item?.id === vizId)) {
      setReportLayout(prev => [...prev, { ...viz, position: prev?.length }]);
    }
  };

  const handleRemoveFromLayout = (vizId) => {
    setReportLayout(prev => prev?.filter(item => item?.id !== vizId));
  };

  const handleLoadTemplate = (template) => {
    setReportName(template?.name);
    setReportLayout(template?.components?.map((compId, idx) => {
      const viz = visualizationTypes?.find(v => v?.id === compId);
      return { ...viz, position: idx };
    }));
  };

  const handleBuildReport = () => {
    const reportData = {
      name: reportName || 'Untitled Report',
      dataSources: selectedDataSources,
      layout: reportLayout,
      format: exportFormat,
      createdAt: new Date()?.toISOString()
    };

    if (exportFormat === 'json') {
      const dataStr = JSON.stringify(reportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportName || 'report'}-${Date.now()}.json`;
      link?.click();
      URL.revokeObjectURL(url);
    } else {
      alert(`Building ${exportFormat?.toUpperCase()} report: ${reportName || 'Untitled Report'}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
            <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Report Configuration</h3>
            <div className="space-y-4">
              <Input
                label="Report Name"
                placeholder="Enter report name"
                value={reportName}
                onChange={(e) => setReportName(e?.target?.value)}
              />

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Select Data Sources</label>
                <div className="grid grid-cols-2 gap-3">
                  {dataSources?.map((source) => (
                    <div 
                      key={source?.id}
                      onClick={() => handleDataSourceToggle(source?.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-smooth ${
                        selectedDataSources?.includes(source?.id)
                          ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon name={source?.icon} size={16} color="var(--color-primary)" />
                        <span className="text-sm font-medium text-card-foreground">{source?.label}</span>
                        {selectedDataSources?.includes(source?.id) && (
                          <Icon name="CheckCircle" size={14} color="var(--color-success)" className="ml-auto" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Available Visualizations</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {visualizationTypes?.map((viz) => (
                    <div 
                      key={viz?.id}
                      className="p-3 rounded-lg border border-border hover:border-primary transition-smooth"
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: 'var(--color-primary)15' }}
                        >
                          <Icon name={viz?.icon} size={18} color="var(--color-primary)" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-card-foreground mb-1">{viz?.label}</h4>
                          <p className="text-xs text-muted-foreground mb-2">{viz?.description}</p>
                          <Button 
                            variant="outline" 
                            size="xs" 
                            iconName="Plus"
                            onClick={() => handleAddToLayout(viz?.id)}
                            disabled={reportLayout?.some(item => item?.id === viz?.id)}
                          >
                            Add to Report
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
            <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Report Templates</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {reportTemplates?.map((template) => (
                <div 
                  key={template?.id}
                  className="p-4 rounded-lg border border-border hover:border-primary transition-smooth cursor-pointer"
                  onClick={() => handleLoadTemplate(template)}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="FileText" size={16} color="var(--color-accent)" />
                    <h4 className="text-sm font-semibold text-card-foreground">{template?.name}</h4>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{template?.description}</p>
                  <div className="flex items-center gap-1">
                    {template?.components?.slice(0, 3)?.map((comp, idx) => (
                      <div key={idx} className="w-6 h-6 rounded bg-muted flex items-center justify-center">
                        <Icon name={visualizationTypes?.find(v => v?.id === comp)?.icon || 'Box'} size={12} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
            <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Report Layout</h3>
            <div className="space-y-3 mb-4">
              {reportLayout?.length === 0 ? (
                <div className="p-8 rounded-lg border-2 border-dashed border-border text-center">
                  <Icon name="LayoutGrid" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Drag components here or click "Add to Report"</p>
                </div>
              ) : (
                reportLayout?.map((item, idx) => (
                  <div 
                    key={item?.id}
                    className="p-3 rounded-lg border border-border bg-muted/50 hover:border-primary transition-smooth"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="GripVertical" size={14} />
                        <span>#{idx + 1}</span>
                      </div>
                      <Icon name={item?.icon} size={16} color="var(--color-primary)" />
                      <span className="text-sm font-medium text-card-foreground flex-1">{item?.label}</span>
                      <button 
                        onClick={() => handleRemoveFromLayout(item?.id)}
                        className="text-error hover:bg-error/10 rounded p-1 transition-smooth"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="space-y-4">
              <Select
                label="Export Format"
                options={exportFormatOptions}
                value={exportFormat}
                onChange={setExportFormat}
              />

              <Button 
                variant="default" 
                iconName="Sparkles" 
                fullWidth
                onClick={handleBuildReport}
                disabled={reportLayout?.length === 0 || selectedDataSources?.length === 0}
              >
                Build Custom Report
              </Button>
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
            <h3 className="text-sm font-semibold text-card-foreground mb-3">Report Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Data Sources:</span>
                <span className="font-medium text-card-foreground">{selectedDataSources?.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Components:</span>
                <span className="font-medium text-card-foreground">{reportLayout?.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Format:</span>
                <span className="font-medium text-card-foreground uppercase">{exportFormat}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportBuilderPanel;